import React, { useEffect, useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import CustomButton from '../../../components/CustomButton';
import { translate } from '../../../services/translate';
import FilterInput from '../../../components/Filter/components/FilterInput';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { DeacParamList } from '../../../typings/Params';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { Pagination } from '../../../components/Pagination';
import MyDataTable from '../../../components/MyDataTable';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import { deacService, VacancyCancellationReasonsType } from '../../../services/central-api/deac';
import Toast from 'react-native-toast-message';
import VacancyCancellationReasonModal from './VacancyCancellationReasonsModal';
import DeleteVacancyCancellationReasonModal from './DeleteVacancyCancellationReasonsModal';
import Container from '../../../components/Container';
import Card from '../../../components/Card';
import Header from '../../../components/Header';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useAuth } from '../../../context/Auth';
import { useQuery } from '@tanstack/react-query';

export default function VacancyCancellationReasons({ navigation }: { navigation: Navigation; }) {
    const route = useRoute<RouteProp<DeacParamList, 'VacancyCancellationReasons'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<DeacParamList['VacancyCancellationReasons']>({
        ...route.params,
        page: Math.max(+route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [selectedVacancyCancellationReason, setSelectedVacancyCancellationReason] = useState<VacancyCancellationReason | undefined>(undefined);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['deacVacancyCancellationReasons', filters],
        queryFn: () => deacService.getVacancyCancellationReasons(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch, isError, error } = queryResult;

    useEffect(() => {
        if (isError) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }, [error, isError]);

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    function getPurposeText(purpose: VacancyCancellationReasonsType) {
        switch (purpose) {
            case VacancyCancellationReasonsType.APPLICATION_APP_AGENT: return translate('appAgentApplicationCancellationReason');
            case VacancyCancellationReasonsType.APPLICATION_WEB_PANEL: return translate('webPanelApplicationCancellationReason');
            case VacancyCancellationReasonsType.VACANCY_WEB_PANEL: return translate('webPanelVacancyCancellationReason');
        }
    }

    return (
        <Container>
            <Header
                title={translate('vacancyCancellationReasons')}
                actions={<CustomButton
                    text={translate('registerCancellationReasons')}
                    onPress={() => {
                        setSelectedVacancyCancellationReason(undefined);
                        setIsModalVisible(true);
                    }}
                />}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredCancellationReasons')}
                    zIndex={2}
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('reason'),
                                cell: row => row.name,
                            },
                            {
                                name: translate('purpose'),
                                cell: row => getPurposeText(row.purpose),
                                grow: 4,
                            },
                            {
                                name: '',
                                cell: row => {
                                    const actions: Action[] = [
                                        {
                                            content: translate('editInformation'),
                                            onPress: () => {
                                                setSelectedVacancyCancellationReason(row);
                                                setIsModalVisible(true);
                                            }
                                        },
                                        {
                                            content: translate('delete'),
                                            variant: 'danger',
                                            onPress: () => {
                                                setSelectedVacancyCancellationReason(row);
                                                setIsDeleteModalVisible(true);
                                            },
                                        }
                                    ];

                                    return (
                                        <DropdownButton actions={actions} />
                                    );
                                },
                                wrap: true,
                                button: true
                            }
                        ]}
                        data={data?.rows || []}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        currentPage={filters.page}
                        totalItems={data?.count || 0}
                        setPage={page => {
                            setFilters(old => ({ ...old, page }));
                            navigation.setParams({ ...route.params, page: page + 1 });
                        }}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                            navigation.setParams({ ...route.params, limit: pageSize, page: 1 });
                        }}
                    />}
                />
            </Card>
            <VacancyCancellationReasonModal
                vacancyCancellationReasonId={selectedVacancyCancellationReason?.id}
                onConfirm={refetch}
                isModalVisible={isModalVisible}
                setModalVisible={setIsModalVisible}
            />
            {selectedVacancyCancellationReason && <DeleteVacancyCancellationReasonModal
                onConfirm={refetch}
                vacancyCancellationReason={selectedVacancyCancellationReason}
                isModalVisible={isDeleteModalVisible}
                setModalVisible={setIsDeleteModalVisible}
            />}
        </Container>
    );
}
